import React, { useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from "react-leaflet";
import { Loader2, LocateFixed } from "lucide-react";
import { Button } from "src/components/ui/button";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

// Bundlers don't resolve Leaflet's default icon paths; wire them explicitly.
const DefaultIcon = L.icon({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

function ClickToPlace({ onChange }) {
  useMapEvents({
    click(e) {
      onChange(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// "Locate me" control: centers the map on the device's GPS position and moves
// the pin there. Overlaid on the map (top-right) like the zoom control.
function LocateControl({ onChange }) {
  const map = useMap();
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState(null);

  function locate() {
    if (!navigator.geolocation) {
      setError("Location isn't supported by this browser.");
      return;
    }
    setError(null);
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        onChange(latitude, longitude);
        map.setView([latitude, longitude], Math.max(map.getZoom(), 17));
        setLocating(false);
      },
      (err) => {
        setError(
          err.code === err.PERMISSION_DENIED
            ? "Location permission denied."
            : "Couldn't get your location."
        );
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }

  return (
    <div className="leaflet-top leaflet-right">
      <div className="leaflet-control leaflet-bar" style={{ margin: 10 }}>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={locate}
          disabled={locating}
          title="Use my current location"
          aria-label="Use my current location"
          className="h-auto w-auto rounded-none hover:bg-white"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 34,
            height: 34,
            background: "#fff",
            cursor: locating ? "default" : "pointer",
            border: "none",
          }}
        >
          {locating ? (
            <Loader2 className="h-4 w-4 animate-spin" style={{ color: "#4f39f6" }} />
          ) : (
            <LocateFixed className="h-4 w-4" style={{ color: "#374151" }} />
          )}
        </Button>
      </div>
      {error && (
        <div
          className="leaflet-control"
          style={{
            margin: 10,
            marginTop: 0,
            maxWidth: 200,
            background: "#fff",
            color: "#dc2626",
            fontSize: 11,
            lineHeight: 1.3,
            padding: "4px 8px",
            borderRadius: 6,
            boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

export function GeofenceMap({ latitude, longitude, radiusMeters, onChange, height = 280 }) {
  const center = useMemo(() => [latitude, longitude], [latitude, longitude]);

  return (
    <div style={{ height, width: "100%", borderRadius: 8, overflow: "hidden", position: "relative", zIndex: 0 }}>
      <MapContainer
        center={center}
        zoom={16}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; OpenStreetMap contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <ClickToPlace onChange={onChange} />
        <LocateControl onChange={onChange} />
        <Marker
          position={center}
          draggable
          eventHandlers={{
            dragend(e) {
              const { lat, lng } = e.target.getLatLng();
              onChange(lat, lng);
            },
          }}
        />
        <Circle
          center={center}
          radius={radiusMeters}
          pathOptions={{ color: "#4f39f6", fillColor: "#4f39f6", fillOpacity: 0.12 }}
        />
      </MapContainer>
    </div>
  );
}
